import React, { useMemo } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, LabelList } from "recharts";

const COLOR_PERF = "#F6615F";

const calcPercent = (rec) => {
  const yes = Number(rec.num_yes ?? 0);
  const no = Number(rec.num_no ?? 0);
  const na = Number(rec.num_na ?? 0);
  const sum = yes + no + na;
  if (sum <= 0) return 0;
  return Math.round((100 * yes) / sum);
};

const calculateNonZeroAverage = (items, key) => {
  const nonZeroValues = items
    .map((item) => Number(item?.[key] ?? 0))
    .filter((value) => Number.isFinite(value) && value !== 0);

  if (!nonZeroValues.length) {
    return 0;
  }

  const total = nonZeroValues.reduce((sum, value) => sum + value, 0);
  return Math.round(total / nonZeroValues.length);
};

const TPerformanceChart = ({ records = [], actionCards = [], lang = "dk", height = 640 }) => {
  const uniqueActionCards = useMemo(() => {
    const seen = new Set();
    return actionCards.filter((card) => {
      const key = String(card?.ac_no ?? "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [actionCards]);

  const data = useMemo(() => {
    const grouped = uniqueActionCards.map((card) => {
      const acNo = String(card.ac_no);
      const cardLabel = lang === "dk" ? card.danish_short_name : card.english_short_name;
  
      // 🔥 Step 1: Filter records for this card
      const filtered = records.filter(
        (r) => String(r.action_card_number ?? "") === acNo
      );
  
      // 🔥 Step 2: Sum all values
      const totals = filtered.reduce(
        (acc, r) => {
          acc.yes += Number(r.num_yes ?? 0);
          acc.no += Number(r.num_no ?? 0);
          acc.na += Number(r.num_na ?? 0);
          return acc;
        },
        { yes: 0, no: 0, na: 0 }
      );
  
      // 🔥 Step 3: Apply your calcPercent on total
      const value = calcPercent({
        num_yes: totals.yes,
        num_no: totals.no,
        num_na: totals.na,
      });
  
      return {
        acNo,
        category: cardLabel,
        value,
      };
    });
  
    if (!grouped.length) {
      return grouped;
    }

    return [
      ...grouped,
      {
        acNo: "total",
        category: lang === "dk" ? "Total" : "Total",
        value: calculateNonZeroAverage(grouped, "value"),
      },
    ];
  }, [records, uniqueActionCards, lang]);

  return (
    <div className="risk-chart-shell" style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 24, right: 20, left: 20, bottom: 70 }}>
          <XAxis
            dataKey="category"
            angle={-45}
            textAnchor="end"
            interval={0}
            height={110}
            tick={{ fontSize: 14, fontWeight: 600 }}
            tickMargin={12}
          />
          <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
          <Tooltip formatter={(v) => `${v}%`} />
          <Bar dataKey="value" name={lang === "dk" ? "Ja i %" : "Yes %"} fill={COLOR_PERF} radius={[6, 6, 0, 0]} isAnimationActive={false}>
            <LabelList
              dataKey="value"
              position="top"
              formatter={(v) => `${v}%`}
              style={{ fill: "#1d1d1d", fontSize: 14, fontWeight: 700 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TPerformanceChart;
